import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCancelPicking } from '../../hooks/useCancelPicking';
import { Preparation } from '../../models';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithPreparation: (preparation: Preparation) => void;
}

interface CancelPickingModalProps {
  onCancel?: (preparation: Preparation) => void;
}

const CancelPickingModal = forwardRef<ExtendedModalCenterRef, CancelPickingModalProps>(({ onCancel }, ref) => {
  const { t } = useTranslation('pickingModule');
  const cancelMutation = useCancelPicking();
  const { toast } = useToast();

  const internalModalRef = useRef<ModalCenterRef>(null);
  const [preparation, setPreparation] = useState<Preparation | null>(null);

  const resetForm = () => {
    setPreparation(null);
    cancelMutation.reset();
  };

  const handleClose = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleCancelPicking = async () => {
    if (!preparation) {
      toast({
        title: t('errors.generic'),
        description: 'No se pudo obtener la información de la preparación',
        variant: 'destructive',
      });
      return;
    }

    const result = await cancelMutation.mutateAsync(preparation.id);

    if (result.success) {
      toast({
        title: t('cancel.success_title'),
        description: result.message,
        variant: 'default',
      });

      if (onCancel) {
        onCancel({ ...preparation, status: 0 });
      }

      handleClose();
    } else {
      toast({
        title: t('cancel.error_title'),
        description: result.message,
        variant: 'destructive',
      });
    }
  };

  const openWithPreparation = useCallback((preparationData: Preparation) => {
    setPreparation(preparationData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithPreparation,
    }),
    [openWithPreparation],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('cancel.modal_title')} type="error">
      <div className="space-y-4">
        <p className="text-sm text-text">
          {t('cancel.confirmation')} <strong>{preparation?.code}</strong>?
        </p>
        <p className="text-sm text-text">{t('cancel.confirmation_subtitle')}</p>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            name="cancel"
            className="w-full sm:w-auto"
            disabled={cancelMutation.isPending}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            name="cancel-picking-btn-confirm"
            onClick={handleCancelPicking}
            className="w-full sm:w-auto"
            disabled={cancelMutation.isPending}
            data-testid={`cancel-picking-button-${preparation?.id}`}
          >
            {cancelMutation.isPending ? t('cancel.canceling') : t('cancel.confirm_button')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default CancelPickingModal;
CancelPickingModal.displayName = 'CancelPickingModal';
